import type { ReactNode } from 'react';
import { Paper, Stack, Text, Group } from '../../../core';
import { faCopy } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { surfaceHeaderProps } from '../SurfaceHeader';

export interface CopyQuoteProps {
  readonly accountName: string;
  /** Source quote's number/limit/premium — shown as a one-line context
   * subtitle, never as a comparison row (the original bound quote is not
   * one of the options; matches the real v1 QuoteDraftCard header). */
  readonly sourceQuoteNumber?: string;
  readonly sourceLimit?: string;
  readonly sourcePremium?: string;
  /** One or more QuoteOption elements — the agent controls how many. */
  readonly children: ReactNode;
}

/**
 * Copy Quote — a comparison of quote options generated by copying an
 * existing quote. Each option (a QuoteOption child) can be generated
 * individually.
 */
export function CopyQuote({
  accountName,
  sourceQuoteNumber,
  sourceLimit,
  sourcePremium,
  children,
}: CopyQuoteProps) {
  return (
    <Paper
      radius="md"
      p="lg"
      withBorder
      style={{
        width: '100%',
        borderColor: 'var(--mantine-color-blue-2)',
      }}
    >
      <Group gap="xs" {...surfaceHeaderProps('lg', 'sm')}>
        <FontAwesomeIcon
          icon={faCopy}
          style={{
            color: 'var(--mantine-color-blue-6)',
            width: 18,
            height: 18,
          }}
        />
        <Text fw={700} fz={13} c="blue.6" style={{ letterSpacing: 0.5 }}>
          COPY QUOTE
        </Text>
        <Text fz={13} c="dimmed">
          {accountName}
        </Text>
      </Group>

      {(sourceLimit || sourcePremium) && (
        <Text fz={12} c="dimmed" mb="lg">
          {`Current${
            sourceQuoteNumber ? ` (${sourceQuoteNumber})` : ''
          }: limit ${sourceLimit || 'n/a'}, Total Premium ${
            sourcePremium || 'n/a'
          }`}
        </Text>
      )}

      <Stack gap="sm" mt={sourceLimit || sourcePremium ? undefined : 'lg'}>
        {children}
      </Stack>
    </Paper>
  );
}
